import { request } from "./api";

export type LibKind = "quiz" | "note" | "past_exam";
export type LibStatus = "draft" | "needs_review" | "published" | "rejected";
export type Band = "high" | "medium" | "low" | "unscored";

export type LibItem = {
  id: string;
  external_id: string;
  kind: string;
  status: LibStatus;
  raw_status: "draft" | "published" | "rejected";
  confidence: number | null;
  band: Band;
  chapter_order: number | null;
  chapter_title: string;
  topic: string;
  difficulty: string | null;
  has_figure: boolean;
  has_source: boolean;
  text: string;
  reasons: string[];
  origin: string;
  mode: "pipeline" | "ai_api" | "unknown";
  model: string | null;
  number: number | string | null;
  updated_at: string | null;
};

export type LibFilter = {
  status?: string;
  band?: string;
  min_conf?: number;
  max_conf?: number;
  difficulty?: string;
  figure?: "yes" | "no";
  source?: "yes" | "no";
  mode?: "pipeline" | "ai_api" | "unknown";
  chapter?: string;
  topic?: string;
  q?: string;
};

export type LibFacets = {
  status: Record<string, number>;
  band: Record<string, number>;
  difficulty: Record<string, number>;
  figure: Record<string, number>;
  source: Record<string, number>;
  mode: Record<string, number>;
  chapters: { key: string; order: number | null; title: string; count: number; byDifficulty: Record<string, number> }[];
  topics: Record<string, number>;
};

export type LibList = { items: LibItem[]; total: number; all: number; capped: boolean; matching_ids: string[]; facets: LibFacets; bands: { high: number; medium: number } };

export type BulkAction = "approve" | "reject" | "send_back" | "regenerate" | "set_difficulty" | "move_chapter";

export type BulkBody = {
  kind: LibKind;
  action: BulkAction;
  ids?: string[];
  filter?: LibFilter;
  include_low?: boolean;
  note?: string;
  difficulty?: string;
  chapter_order?: number;
  chapter_title?: string;
  dry_run?: boolean;
  on_conflict?: "append" | "replace" | "new_chapter";
  confirm_replace?: boolean;
};

export type BulkPreview = { total: number; will_change: number; left_out_low_confidence: number; unchanged: number; action: BulkAction };
export type BulkResult = BulkPreview & { changed?: number; batch_id?: string | null; undo_minutes?: number; warnings?: string[]; blocked?: { reason: string; item_ids: string[] }[] };

function query(kind: LibKind, f: LibFilter): string {
  const p = new URLSearchParams({ kind });
  for (const [k, v] of Object.entries(f)) if (v !== undefined && v !== "") p.set(k, String(v));
  return p.toString();
}

export const courseLibraryApi = {
  list: (token: string, courseId: string, kind: LibKind, f: LibFilter) =>
    request<LibList>(`/admin/courses/${courseId}/library?${query(kind, f)}`, {}, token),
  bulk: (token: string, courseId: string, body: BulkBody) =>
    request<BulkResult | BulkPreview>(`/admin/courses/${courseId}/library/bulk`, { method: "POST", body: JSON.stringify(body) }, token),
  undo: (token: string, courseId: string, batchId: string) =>
    request<{ restored: number; note: string | null }>(`/admin/courses/${courseId}/library/undo`, { method: "POST", body: JSON.stringify({ batch_id: batchId }) }, token),
};

export const courseLibraryItemApi = {
  duplicate: (token: string, courseId: string, id: string) =>
    request<LibItem>(`/admin/courses/${courseId}/library/items/${id}/duplicate`, { method: "POST" }, token),
  remove: (token: string, courseId: string, id: string) =>
    request<{ deleted: boolean }>(`/admin/courses/${courseId}/library/items/${id}`, { method: "DELETE" }, token),
};

export const BAND_LABEL: Record<Band, string> = { high: "High", medium: "Medium", low: "Low", unscored: "Not scored" };
export const STATUS_LABEL: Record<LibStatus, string> = { draft: "Draft", needs_review: "Needs review", published: "Approved", rejected: "Rejected" };

/** Admin-only label for which mode produced an item. */
export const ORIGIN_LABEL: Record<string, string> = { pipeline: "Pipeline", ai_api: "AI API", unknown: "Unknown" };
